import { useContext, useEffect, useRef, useState } from 'react'
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, TextField, Typography } from '@mui/material'

import { AppContext } from '/providers/app-context'
import { credentialOnboarding, generateClientCsr, getClientCertificates, uploadOwnAppleCertificate } from '/providers/studio-api'

export const DifferenceDialog = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
    <DialogTitle>What’s the difference?</DialogTitle>
    <DialogContent dividers>
      <Stack spacing={2}>
        <Typography>Secure credentials are required to register wallet passes with Apple and Google.</Typography>
        <Typography>You can either allow Smartix to generate and manage these credentials for you (most clients choose this option), or you may provide your own credentials issued to your own Apple and Google accounts.</Typography>
        <Typography>Using Smartix-managed credentials is the simplest option and makes no difference to how your pass data is stored or managed. Using your own credentials gives your organisation direct ownership of its Apple and Google Wallet identities and provides a potential migration path should you ever stop using Smartix. Please speak to us if you require more information about this.</Typography>
        <Typography>Having your own Apple and Google developer accounts does have a cost. As of September 2026, Google charge a one-off US$25 fee and Apple charge £79 per year.</Typography>
        <Typography>If you wish to use your own credentials, you can still start by using Smartix credentials for initial evaluation. To switch later, all templates and passes created in the evaluation account must be deleted first. You can request a switch by contacting Smartix Support.</Typography>
      </Stack>
    </DialogContent>
    <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
  </Dialog>
)

const WalletCredentialSetup = ({ onEditAccountDetails }) => {
  const { apiSource, apiKey, client, setClient } = useContext(AppContext)
  const [choice, setChoice] = useState(client.wallet_credential_type ?? '')
  const [showDifference, setShowDifference] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [googleIssuerId, setGoogleIssuerId] = useState(client.google_issuer_verified ? client.google_issuer_id ?? '' : '')
  const [googleVerified, setGoogleVerified] = useState(Boolean(client.google_issuer_verified))
  const [applePassTypeId, setApplePassTypeId] = useState(client.apple_pass_type_id ?? '')
  const [appleTeamId, setAppleTeamId] = useState('')
  const [appleEmail, setAppleEmail] = useState('')
  const [appleSaved, setAppleSaved] = useState(Boolean(client.apple_pass_type_id))
  const [csrReady, setCsrReady] = useState(false)
  const [certificateUploaded, setCertificateUploaded] = useState(false)
  const [certificateFile, setCertificateFile] = useState(null)
  const inputRef = useRef(null)

  useEffect(() => {
    if (!client.apple_pass_type_id) return
    const loadAppleProgress = async () => {
      const response = await getClientCertificates({ apiSource, apiKey })
      if (!response?.ok) return
      const apple = response.certificate?.apple
      if (!apple) return
      setApplePassTypeId(apple.passTypeId ?? '')
      setAppleTeamId(apple.teamId ?? '')
      setAppleEmail(apple.appleEmail ?? '')
      setAppleSaved(Boolean(apple.passTypeId))
      setCsrReady(Boolean(apple.csrAvailable))
      setCertificateUploaded(Boolean(apple.configured))
    }
    loadAppleProgress()
  }, [apiSource, apiKey, client.apple_pass_type_id])

  const updateClient = response => {
    if (response?.client) setClient(response.client)
  }

  const run = async fn => {
    setBusy(true)
    setError('')
    setMessage('')
    try {
      const response = await fn()
      if (!response?.ok) throw new Error(response?.error?.message ?? response?.error ?? 'The request could not be completed')
      updateClient(response)
      return response
    }
    catch (err) {
      setError(err.message ?? 'The request could not be completed')
      return null
    }
    finally { setBusy(false) }
  }

  const chooseSmartix = async () => {
    setChoice('smartix')
    const response = await run(() => credentialOnboarding({ apiSource, apiKey, action: 'use_smartix' }))
    if (response) setMessage('Smartix has created and installed your Apple Wallet credentials. Your Studio account is ready.')
  }

  const saveAppleDetails = async () => {
    const response = await run(() => credentialOnboarding({
      apiSource, apiKey, action: 'save_apple_details', apple_pass_type_id: applePassTypeId, apple_team_id: appleTeamId, apple_email: appleEmail
    }))
    if (response) {
      setAppleSaved(true)
      setCsrReady(false)
      setCertificateUploaded(false)
      setMessage('Apple details saved. Generate a CSR, submit it to Apple, then upload the CER file Apple returns.')
    }
  }

  const verifyGoogle = async () => {
    const response = await run(() => credentialOnboarding({ apiSource, apiKey, action: 'verify_google', google_issuer_id: googleIssuerId }))
    if (response) {
      setGoogleVerified(true)
      setMessage(`Google Wallet issuer ${response.google?.issuer_id} has been verified.`)
    } else {
      setGoogleIssuerId('')
      setGoogleVerified(false)
    }
  }

  const generateCsr = async () => {
    const replacePending = csrReady
      ? window.confirm('A CSR is already pending. Generating a new CSR will invalidate it, so Apple cannot use the previous CSR. Continue?')
      : false
    if (csrReady && !replacePending) return
    const response = await run(() => generateClientCsr({ apiSource, apiKey, replacePending }))
    if (!response) return
    const url = URL.createObjectURL(new Blob([response.csr_pem], { type: 'application/x-pem-file' }))
    const link = document.createElement('a')
    link.href = url
    link.download = response.filename
    link.click()
    URL.revokeObjectURL(url)
    setCsrReady(true)
    setMessage('CSR generated and downloaded. Upload it to Apple Developer, then upload Apple’s CER file here.')
  }

  const readCertificate = async event => {
    const file = event.target.files?.[0]
    if (!file) return
    if (file.name.toLowerCase().endsWith('.cer')) {
      const bytes = new Uint8Array(await file.arrayBuffer())
      let binary = ''
      bytes.forEach(byte => { binary += String.fromCharCode(byte) })
      setCertificateFile({ name: file.name, content: btoa(binary) })
    } else {
      setCertificateFile({ name: file.name, content: await file.text() })
    }
  }

  const uploadCertificate = async () => {
    const response = await run(() => uploadOwnAppleCertificate({ apiSource, apiKey, pem: certificateFile?.content }))
    if (response) {
      setCertificateUploaded(true)
      setCertificateFile(null)
      if (inputRef.current) inputRef.current.value = ''
      setMessage('Apple signing certificate verified and uploaded.')
    }
  }

  const completeByo = async () => {
    const response = await run(() => credentialOnboarding({ apiSource, apiKey, action: 'complete_byo' }))
    if (response) setMessage('Wallet credentials are verified. Your Studio account is ready.')
  }

  return (
    <div className='mx-auto box-border flex min-h-full w-full max-w-6xl flex-col gap-6 p-8 pb-24'>
      {onEditAccountDetails ? <div><Button variant='outlined' onClick={onEditAccountDetails} disabled={busy}>Edit account details or credential choice</Button></div> : null}
      <Typography variant='h3'>Set up Wallet credentials</Typography>
      <Typography>Before you can create templates or issue passes, choose how Apple and Google Wallet credentials will be managed for this account.</Typography>
      {error ? <Alert severity='error'>{error}</Alert> : null}
      {message ? <Alert severity='success'>{message}</Alert> : null}

      {!choice ? <div className='flex w-full flex-wrap gap-5'>
        <div className='flex min-w-[300px] flex-1 flex-col gap-4 rounded border border-white/15 p-5'>
          <Typography variant='h5'>Use Smartix credentials</Typography>
          <Typography>Smartix creates and manages an Apple Pass Type ID, signing certificate and Google Wallet issuer configuration for you. This is the recommended option for evaluation and most live accounts.</Typography>
          <Button variant='contained' onClick={chooseSmartix} disabled={busy}>Use Smartix credentials</Button>
        </div>
        <div className='flex min-w-[300px] flex-1 flex-col gap-4 rounded border border-white/15 p-5'>
          <Typography variant='h5'>Bring your own credentials</Typography>
          <Typography>Use your organisation’s Apple Developer and Google Wallet accounts. You will configure and verify both before Studio is available.</Typography>
          <Button variant='outlined' onClick={() => setChoice('byo')} disabled={busy}>Use my own credentials</Button>
        </div>
        <div className='basis-full'><Button onClick={() => setShowDifference(true)}>What’s the difference?</Button></div>
      </div> : null}

      {choice === 'smartix' ? <Stack direction='row' spacing={2} alignItems='center'>
        <Alert severity='info' className='flex-1'>Creating your credentials can take a moment. If provisioning fails, you can retry here; Smartix support is notified automatically.</Alert>
        <Button variant='contained' onClick={chooseSmartix} disabled={busy}>{busy ? 'Creating…' : 'Retry credential setup'}</Button>
      </Stack> : null}

      {choice === 'byo' ? <div className='flex w-full flex-col gap-6'>
        <Alert severity='info'>You can complete Google and Apple setup independently and return later. Your account remains in onboarding until both are verified.</Alert>
        <div className='flex flex-wrap items-start gap-5'>
          <div className='flex min-w-[320px] flex-1 flex-col gap-4'>
            <Typography variant='h5'>1. Google Wallet</Typography>
            <Typography variant='body2'>Add the Smartix service account as a Developer in your Google Wallet issuer account, then enter your Issuer ID.</Typography>
            <TextField label='Google Wallet Issuer ID' value={googleIssuerId} onChange={event => { setGoogleIssuerId(event.target.value); setGoogleVerified(false) }} disabled={busy} />
            <Button variant='outlined' onClick={verifyGoogle} disabled={busy || !googleIssuerId}>Verify Google Wallet access</Button>
            {googleVerified ? <Alert severity='success'>Google Wallet access verified.</Alert> : null}
          </div>
          <Divider flexItem orientation='vertical' />
          <div className='flex min-w-[320px] flex-1 flex-col gap-4'>
            <Typography variant='h5'>2. Apple Wallet</Typography>
            <Typography variant='body2'>Enter your Apple details, generate a Smartix CSR, use it in Apple Developer, then upload Apple’s resulting CER file.</Typography>
            <TextField label='Apple Pass Type ID' value={applePassTypeId} onChange={event => { setApplePassTypeId(event.target.value); setAppleSaved(false) }} disabled={busy} />
            <TextField label='Apple Team ID' value={appleTeamId} onChange={event => { setAppleTeamId(event.target.value); setAppleSaved(false) }} disabled={busy} />
            <TextField label='Apple account email' value={appleEmail} onChange={event => { setAppleEmail(event.target.value); setAppleSaved(false) }} disabled={busy} />
            <Button variant='outlined' onClick={saveAppleDetails} disabled={busy || !applePassTypeId || !appleTeamId || !appleEmail}>Save Apple details</Button>
            <Button variant='outlined' onClick={generateCsr} disabled={busy || !appleSaved}>Generate and download CSR</Button>
            <input ref={inputRef} type='file' accept='.cer,.pem' onChange={readCertificate} disabled={busy || !appleSaved} />
            {certificateFile ? <Typography variant='body2'>Selected: {certificateFile.name}</Typography> : null}
            <Button variant='contained' onClick={uploadCertificate} disabled={busy || !certificateFile || !appleSaved}>Upload and verify Apple CER</Button>
            {certificateUploaded ? <Alert severity='success'>Apple certificate verified.</Alert> : null}
          </div>
        </div>
        <div className='flex items-center justify-between gap-4'>
          <Typography variant='body2'>Need detailed setup guidance? The downloadable setup guide will be provided here.</Typography>
          <Button variant='contained' onClick={completeByo} disabled={busy || !googleVerified || !certificateUploaded}>Save and proceed to Smartix Studio</Button>
        </div>
      </div> : null}
      <DifferenceDialog open={showDifference} onClose={() => setShowDifference(false)} />
    </div>
  )
}

export default WalletCredentialSetup
